import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import { alpha, useTheme } from '@mui/material/styles';

import Chat from './Chat';
import HomeHeader from '../util/HomeHeader';
import { mislecAPI } from '../../util/axios';
import Iconify from '../../theme/iconify/iconify';

const Home = ({ user, updateUser }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();
    const [tags, setTags] = useState([]);

    React.useEffect(() => {
        mislecAPI.get('/api/home/tags').then((res) => {
            setTags(res.data.tags);
        }).catch((err) => {
            // TODO: error handling?
            console.log('err', err);
        });
    }, []);

    return (
        <>
            <HomeHeader user={user} updateUser={updateUser} bgColor={theme.palette.primary.lighter} />

            <Grid container
                sx={{
                    padding: {
                        md: 15,
                        xs: 5
                    },
                    paddingLeft: {
                        md: 15,
                        xs: 5
                    },
                    paddingRight: {
                        md: 15,
                        xs: 5
                    },
                    backgroundColor: theme.palette.primary.lighter
                }}
            >
                <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Box
                        component='img'
                        src='/assets/images/DR-mISLec.png'
                        alt={t('home.image_alt')}
                        sx={{
                            width: {
                                xl: '550px',
                                md: '325px',
                                sm: '350px',
                                xs: '250px'
                            },
                            height: 'auto',
                            display: 'block',
                            maxWidth: '100%',
                            objectFit: 'contain'
                        }}
                    />
                </Grid>

                <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Typography variant={'h3'} sx={{ paddingBottom: 2, paddingTop: { xs: 3 } }}>
                        {t('home.cover.title')}
                    </Typography>
                    <Typography sx={{ paddingBottom: 1 }}>
                        {t('home.cover.description')}
                    </Typography>
                    <Typography>
                        🚀 {t('home.cover.additional.learn')}
                    </Typography>
                    <Typography>
                        🧠 {t('home.cover.additional.master')}
                    </Typography>
                    <Typography>
                        💡 {t('home.cover.additional.build')}
                    </Typography>
                    <Typography sx={{ paddingTop: 1 }}>
                        {t('home.cover.additional.after')}
                    </Typography>

                    <Grid sx={{
                        display: 'flex',
                        justifyContent: { md: 'start', xs: 'center' },
                        flexWrap: 'wrap',
                        gap: 2,
                        paddingTop: 4
                    }}>
                        <LoadingButton
                            size='large'
                            type='submit'
                            variant='contained'
                            color='inherit'
                            onClick={() => { navigate('/course') }}
                        >
                            {t('home.cover.call')}
                        </LoadingButton>
                        <Button
                            variant='large'
                            endIcon={<Iconify icon='weui:arrow-filled' sx={{ transform: 'rotate(90deg)' }} />}
                            onClick={() => {
                                document.getElementById('mISLec_landing_chat')?.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'start',
                                });
                            }}
                        >
                            {t('home.cover.chat')}
                        </Button>
                    </Grid>
                </Grid>
            </Grid>

            <Grid container id='mISLec_landing_chat' sx={{
                display: 'flex',
                justifyContent: 'center'
            }}>
                <Grid item xs={12} sx={{
                    display: 'flex',
                    minHeight: '600px',
                    maxHeight: '600px',
                    /* maxWidth: { lg: '1000px', md: 'inherit' } */
                }}>
                    <Chat />
                </Grid>
            </Grid>

            <Grid container sx={{
                padding: {
                    md: 15,
                    xs: 5
                },
                display: 'flex',
                flexDirection: 'row',
                gap: {
                    md: 2,
                    xl: 0
                },
                rowGap: {
                    xs: 2
                },
                justifyContent: 'center'
            }}>
                <Grid item xs={12} sm={12} md={5} xl={4}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Grid sx={{
                        padding: 4,
                        borderRadius: '16px',
                        backgroundColor: theme.palette.primary.lighter,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        maxWidth: '500px',
                        width: '100%',
                        minWidth: '300px',
                        maxHeight: '300px',
                        minHeight: '300px'
                    }}>
                        <img src={'/assets/images/async-computer.png'} alt={'Placeholder'}
                            style={{ width: '210px', height: 'auto', maxWidth: '210px' }} />
                        <Typography variant={'h5'}>
                            {t('home.advantages.asynchronous.title')}
                        </Typography>
                        <Typography align={'center'}>
                            {t('home.advantages.asynchronous.description')}
                        </Typography>
                    </Grid>
                </Grid>
                <Grid item xs={12} md={5} xl={4} sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}>
                    <Grid sx={{
                        padding: 4,
                        borderRadius: '16px',
                        backgroundColor: theme.palette.grey.A200,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        maxWidth: '500px',
                        minWidth: '300px',
                        maxHeight: '300px',
                        minHeight: '300px'
                    }}>
                        <img src={'/assets/images/personalized.png'} alt={'Placeholder'}
                            style={{ width: '150px', height: 'auto', maxWidth: '150px' }} />
                        <Typography variant={'h5'}>
                            {t('home.advantages.personalized.title')}
                        </Typography>
                        <Typography align={'center'}>
                            {t('home.advantages.personalized.description')}
                        </Typography>
                    </Grid>
                </Grid>
                <Grid item xs={12} md={5} xl={4} sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}>
                    <Grid sx={{
                        padding: 4,
                        borderRadius: '16px',
                        backgroundColor: theme.palette.secondary.lighter,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        maxWidth: '500px',
                        minWidth: '300px',
                        maxHeight: '300px',
                        minHeight: '300px'
                    }}>
                        <img src={'/assets/images/mISLec-logo-square.png'} alt={'Placeholder'}
                            style={{ width: '150px', height: 'auto', maxWidth: '150px' }} />
                        <Typography variant={'h5'}>
                            {t('home.advantages.assistant.title')}
                        </Typography>
                        <Typography align={'center'}>
                            {t('home.advantages.assistant.description')}
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>

            <Grid container sx={{
                padding: {
                    md: 15,
                    xs: 5
                },
                paddingLeft: {
                    md: 15,
                    xs: 5
                },
                paddingRight: {
                    md: 15,
                    xs: 5
                },
                backgroundColor: theme.palette.grey.A100
            }}>
                <Grid item xs={12} sx={{ paddingBottom: 3 }}>
                    <Typography variant={'h5'} align={'center'} sx={{ fontSize: '1.25rem !important' }}>
                        {t('home.tags.title')}
                    </Typography>
                </Grid>
                <Grid container sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    columnGap: 1,
                    rowGap: 1
                }}>
                    {tags.length > 0 && tags.map((tag) => {
                        return (
                            <Chip
                                key={`tags-${tag}`}
                                label={t(`profile.tabs.profile.basic.inputs.skills.values.${tag}`)}
                                sx={{
                                    backgroundColor: alpha(theme.palette.secondary.main, 0.2),
                                    fontWeight: '600',
                                    fontSize: '0.85rem',
                                    borderRadius: '8px'
                                }}
                            />
                        )
                    })}
                </Grid>
            </Grid>

            <Grid container sx={{
                padding: {
                    md: 15,
                    xs: 5
                },
                paddingLeft: {
                    md: 15,
                    xs: 5
                },
                paddingRight: {
                    md: 15,
                    xs: 5
                },
                gap: {
                    md: 2,
                    xl: 0
                },
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                rowGap: 4
            }}>
                <Grid item xs={12} md={6}>
                    <Grid sx={{
                        backgroundColor: theme.palette.grey.A100,
                        height: '100%',
                        padding: {
                            xs: 4,
                            md: 8
                        },
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: {
                            xs: 'center',
                            md: 'left'
                        },
                        borderRadius: '16px'
                    }}>
                        <Typography variant={'h5'}>
                            {t('home.touch.title')}
                        </Typography>
                        <Typography sx={{ marginTop: 1 }}>
                            {t('home.touch.description')}
                        </Typography>

                        <Button
                            variant=''
                            onClick={() => {
                                window.location.href = t('home.touch.link')
                            }}
                            sx={{
                                borderRadius: '8px !important',
                                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
                                color: {
                                    xs: 'primary.main'
                                },
                                '&:hover': {
                                    color: 'primary.main',
                                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.15)
                                },
                                marginTop: 3,
                                alignSelf: {
                                    xs: 'center',
                                    md: 'start !important'
                                },
                                width: 'fit-content'
                            }}
                        >
                            {t('home.touch.action')}
                        </Button>
                    </Grid>

                </Grid>

                <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Box
                        component='img'
                        src='/assets/images/people-courses-bg.png'
                        alt={t('home.image_alt')}
                        sx={{
                            width: {
                                xl: '450px',
                                md: '325px',
                                sm: '350px',
                                xs: '250px'
                            },
                            height: 'auto',
                            display: 'block',
                            maxWidth: '100%',
                            objectFit: 'contain'
                        }}
                    />
                </Grid>
            </Grid>
        </>
    )
}

export default Home
